const MAX_DIMENSION = 1200;
const JPEG_QUALITY = 0.85;
// Converting a large HEIC via wasm on a slow device can take tens of seconds.
const HEIC_CONVERT_TIMEOUT_MS = 30_000;
const HEIC_PROBE_TIMEOUT_MS = 1_500;

// 16×16 HEVC-coded HEIC (generated with macOS sips) — genuinely decodable,
// unlike a bare ftyp box, and cheap to probe regardless of the upload's size.
const HEIC_PROBE_DATA_URL =
  'data:image/heic;base64,AAAAJGZ0eXBoZWljAAAAAG1pZjFNaVBybWlhZk1pSEJoZWljAAABhm1ldGEAAAAAAAAAIWhkbHIAAAAAAAAAAHBpY3QAAAAAAAAAAAAAAAAAAAAAJGRpbmYAAAAcZHJlZgAAAAAAAAABAAAADHVybCAAAAABAAAADnBpdG0AAAAAAAEAAAAjaWluZgAAAAAAAQAAABVpbmZlAgAAAAABAABodmMxAAAAAOZpcHJwAAAAxWlwY28AAAATY29scm5jbHgAAgACAAaAAAAADGNsbGkAywBAAAAAFGlzcGUAAAAAAAAAEAAAABAAAAAJaXJvdAAAAAAQcGl4aQAAAAADCAgIAAAAcWh2Y0MBA3AAAACwAAAAAAAe8AD8/fj4AAALA6AAAQAXQAEMAf//A3AAAAMAsAAAAwAAAwAecCShAAEAI0IBAQNwAAADALAAAAMAAAMAHqAUIEHAkwziHuRZVNwICBgCogABAAlEAcBhcshEU2QAAAAZaXBtYQAAAAAAAAABAAEGgQIDBYaEAAAAHmlsb2MAAAAARAAAAQABAAAAAQAAAboAAAAaAAAAAW1kYXQAAAAAAAAAKgAAABYoAa+iREgDcNcLlYkegD5hQcFQEpWA';
const REMOTE_FETCH_TIMEOUT_MS = 30_000;

export const HEIC_UNSUPPORTED_MESSAGE =
  'This browser cannot convert HEIC photos. Please convert the image to JPEG and upload it manually.';

export function isHeicUnsupportedError(error: unknown): error is Error {
  return error instanceof Error && error.message === HEIC_UNSUPPORTED_MESSAGE;
}

async function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timer = setTimeout(() => {
          reject(new Error(HEIC_UNSUPPORTED_MESSAGE));
        }, ms);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export function isHeic(file: File): boolean {
  if (file.type === 'image/heic' || file.type === 'image/heif') return true;
  if (file.type) return false;
  const name = file.name.toLowerCase();
  return name.endsWith('.heic') || name.endsWith('.heif');
}

export function isImage(file: File): boolean {
  const name = file.name.toLowerCase();
  return (
    isHeic(file) ||
    file.type === 'image/jpeg' ||
    file.type === 'image/png' ||
    file.type === 'image/webp' ||
    name.endsWith('.jpg') ||
    name.endsWith('.jpeg') ||
    name.endsWith('.png') ||
    name.endsWith('.webp')
  );
}

// Probes generic HEIC support only; a variant the browser still can't decode
// (e.g. 10-bit HDR) fails downstream where the real decode is error-handled.
function canBrowserDecodeHeic(): Promise<boolean> {
  return withTimeout(
    new Promise<boolean>(resolve => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = HEIC_PROBE_DATA_URL;
    }),
    HEIC_PROBE_TIMEOUT_MS,
  ).catch(() => false);
}

export async function normalizeToBlob(
  file: File,
  {
    convertTimeoutMs = HEIC_CONVERT_TIMEOUT_MS,
  }: { convertTimeoutMs?: number } = {},
): Promise<Blob> {
  if (!isHeic(file)) return file;

  try {
    const heic2any = (await import('heic2any')).default;
    const result = await withTimeout(
      Promise.resolve(heic2any({ blob: file, toType: 'image/jpeg' })),
      convertTimeoutMs,
    );
    if (Array.isArray(result)) return new Blob(result, { type: 'image/jpeg' });
    return result;
  } catch (conversionError) {
    if (await canBrowserDecodeHeic()) return file;
    const error = new Error(HEIC_UNSUPPORTED_MESSAGE);
    error.cause = conversionError;
    throw error;
  }
}

export function isHeicRemoteSource(
  fileName: string,
  contentType?: string | null,
): boolean {
  const type = contentType?.split(';')[0]?.trim().toLowerCase() ?? '';
  if (type === 'image/heic' || type === 'image/heif') return true;
  if (type.startsWith('image/')) return false;
  return /\.hei[cf]$/i.test(fileName);
}

export function replaceHeicExtension(pathOrName: string): string {
  return pathOrName.replace(/\.hei[cf]$/i, '.jpg');
}

export function compressBlob(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const { width, height } = img;
        const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(width * scale);
        canvas.height = Math.round(height * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(reader.result as string);
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', JPEG_QUALITY));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(blob);
  });
}

export async function ensureJpegBlob(file: File): Promise<Blob> {
  if (!isHeic(file)) return file;

  const normalized = await normalizeToBlob(file);
  if (normalized.type === 'image/jpeg') return normalized;

  try {
    const dataUrl = await withTimeout(
      compressBlob(normalized),
      HEIC_CONVERT_TIMEOUT_MS,
    );
    const response = await fetch(dataUrl);
    return new Blob([await response.blob()], { type: 'image/jpeg' });
  } catch (reEncodeError) {
    const error = new Error(HEIC_UNSUPPORTED_MESSAGE);
    error.cause = reEncodeError;
    throw error;
  }
}

export async function normalizeRemoteImage(
  remoteUrl: string,
  fileName: string,
  contentTypeHint = 'image/jpeg',
): Promise<{ blob: Blob; contentType: string; fileName: string }> {
  const response = await fetch(remoteUrl, {
    signal: AbortSignal.timeout(REMOTE_FETCH_TIMEOUT_MS),
  });
  if (!response.ok) {
    throw new Error('Failed to download remote file');
  }

  const blob = await response.blob();
  const resolvedContentType =
    blob.type?.split(';')[0] ||
    response.headers.get('content-type')?.split(';')[0] ||
    contentTypeHint;

  if (!isHeicRemoteSource(fileName, resolvedContentType)) {
    return {
      blob,
      contentType: resolvedContentType,
      fileName,
    };
  }

  const fileType =
    resolvedContentType === 'image/heic' || resolvedContentType === 'image/heif'
      ? resolvedContentType
      : '';
  const file = new File([blob], fileName, { type: fileType });
  const jpegBlob = await ensureJpegBlob(file);

  return {
    blob: jpegBlob,
    contentType: 'image/jpeg',
    fileName: replaceHeicExtension(fileName),
  };
}

export async function processImage(file: File): Promise<string> {
  const blob = await normalizeToBlob(file);
  return compressBlob(blob);
}
